import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Star, ShieldCheck, PencilLine } from "lucide-react";

const StarRow = ({ value, size = 4, testid }) => (
  <div className="flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-${size} h-${size} ${n <= value ? "fill-zinc-200 text-zinc-200" : "text-zinc-700"}`}
      />
    ))}
  </div>
);

const StarInput = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="star-input">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`star-input-${n}`}
        className="p-1 focus:outline-none focus:ring-2 focus:ring-zinc-200"
        aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
      >
        <Star className={`w-7 h-7 transition-transform hover:scale-110 ${n <= value ? "fill-zinc-200 text-zinc-200" : "text-zinc-700"}`} />
      </button>
    ))}
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const initialForm = { name: "", location: "", rating: 5, quote: "", email: "" };

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/reviews?limit=200"),
      api.get("/reviews/stats"),
    ])
      .then(([r, s]) => {
        setReviews(r.data);
        setStats(s.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (window.location.hash === "#write") {
      setTimeout(() => document.getElementById("write-review")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const sorted = useMemo(() => {
    const arr = [...reviews];
    if (sort === "highest") arr.sort((a, b) => b.rating - a.rating);
    else arr.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return arr;
  }, [reviews, sort]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.quote.trim() || form.quote.trim().length < 8) {
      toast.error("Add your name and a review of at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        location: form.location.trim() || null,
        rating: form.rating,
        quote: form.quote.trim(),
        email: form.email.trim() || null,
      });
      toast.success("Thanks — your review is pending approval");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit review";
      toast.error(typeof msg === "string" ? msg : "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="reviews-page">

        {/* Header + aggregate */}
        <div className="border-b border-zinc-800 pb-8 mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-[1px] bg-zinc-500" />
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 uppercase">
                [ 01 · CUSTOMER ARCHIVE · REAL FIT REPORTS ]
              </div>
            </div>
            <h1 className="display-h1 text-5xl sm:text-7xl text-zinc-50">
              Customer <span className="text-zinc-400">Reviews</span>
            </h1>
            <p className="mt-4 max-w-xl font-mono-tech text-[11px] uppercase tracking-[0.25em] text-zinc-400">
              // Real feedback from real customers. All reviews are moderated before going live.
            </p>
          </div>
          <div className="border-2 border-zinc-800 bg-zinc-900/40 px-6 py-5 min-w-[240px]" data-testid="reviews-aggregate">
            <div className="flex items-center gap-3">
              <div className="font-display text-5xl text-zinc-50 leading-none">{stats.count > 0 ? stats.average.toFixed(1) : "—"}</div>
              <div>
                <StarRow value={Math.round(stats.average)} size={4} testid="aggregate-stars" />
                <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                  Based on {stats.count} review{stats.count === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <a
              href="#write-review"
              onClick={(e) => { e.preventDefault(); document.getElementById("write-review")?.scrollIntoView({ behavior: "smooth" }); }}
              className="mt-4 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest bg-zinc-300 text-zinc-950 hover:bg-zinc-200 transition-colors px-4 py-2"
              data-testid="jump-to-write"
            >
              <PencilLine className="w-3.5 h-3.5" /> Write a Review
            </a>
          </div>
        </div>

        {/* Filters */}
        {reviews.length > 1 && (
          <div className="flex flex-wrap gap-3 mb-8" data-testid="reviews-sort-row">
            {[
              { id: "newest", label: "NEWEST" },
              { id: "highest", label: "HIGHEST RATED" },
            ].map((f) => {
              const active = sort === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setSort(f.id)}
                  data-testid={`sort-${f.id}`}
                  className={`font-mono-tech text-xs uppercase tracking-widest px-4 py-2 border-2 transition-colors ${
                    active ? "bg-zinc-100 border-zinc-100 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Reviews grid */}
        {loading ? (
          <div className="text-center py-24 font-mono-tech text-zinc-500 uppercase text-sm">// Loading reviews ...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20 border border-zinc-800 bg-zinc-950" data-testid="reviews-empty">
            <div className="font-display text-3xl sm:text-4xl text-zinc-100 uppercase mb-3">Be the first.</div>
            <p className="max-w-md mx-auto text-zinc-400 text-sm leading-relaxed mb-6">
              We&apos;re building out the fit-report archive. Drop yours below —
              once approved by the team, it goes live for everyone to see.
            </p>
            <a
              href="#write"
              className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-white text-zinc-950 px-5 py-2.5 font-display text-xs tracking-widest uppercase transition-colors"
            >
              Write the first review
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {sorted.map((r) => (
              <div
                key={r.id}
                data-testid={`review-${r.id}`}
                className="border-2 border-zinc-800 bg-zinc-900/40 p-6 hover:border-zinc-300/60 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <StarRow value={r.rating} size={4} testid={`review-stars-${r.id}`} />
                  {r.verified_purchase && (
                    <span
                      className="inline-flex items-center gap-1 border border-emerald-600 text-emerald-500 font-mono-tech text-[10px] uppercase tracking-widest px-2 py-0.5"
                      data-testid={`verified-${r.id}`}
                    >
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <p className="text-zinc-200 leading-relaxed mb-6">&ldquo;{r.quote}&rdquo;</p>
                <div className="border-t border-zinc-800 pt-4 flex items-end justify-between">
                  <div>
                    <div className="font-display text-sm text-zinc-50 uppercase tracking-widest">{r.name}</div>
                    {r.location && (
                      <div className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mt-1">// {r.location}</div>
                    )}
                  </div>
                  <div className="font-mono-tech text-[10px] text-zinc-600 uppercase tracking-widest">{formatDate(r.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Write a Review */}
        <div id="write-review" className="mt-20 border-2 border-zinc-300/60 bg-zinc-900/40 p-6 sm:p-10" data-testid="write-review-section">
          <div className="border-b-2 border-zinc-800 pb-4 mb-6">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 uppercase mb-2">
              [ NEW ENTRY · FIELD REPORT ]
            </div>
            <h2 className="font-display text-3xl sm:text-5xl text-zinc-50 uppercase leading-none tracking-tight">
              Write a Review
            </h2>
            <p className="mt-3 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-zinc-400">
              // Submissions are reviewed by our team before going live.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5 max-w-2xl">
            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Your Rating *</label>
              <StarInput value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono-tech text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Display Name *</label>
                <input
                  type="text"
                  data-testid="review-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={60}
                  required
                  placeholder="e.g. MARCUS_44"
                  className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-zinc-200 outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100 placeholder:text-zinc-600"
                />
              </div>
              <div>
                <label className="block font-mono-tech text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Location (optional)</label>
                <input
                  type="text"
                  data-testid="review-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  maxLength={60}
                  placeholder="e.g. AUSTIN, TX"
                  className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-zinc-200 outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100 placeholder:text-zinc-600"
                />
              </div>
            </div>

            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Email (private — never shown)</label>
              <input
                type="email"
                data-testid="review-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                maxLength={200}
                placeholder="For order verification only — optional"
                className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-zinc-200 outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100 placeholder:text-zinc-600"
              />
            </div>

            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Your Review *</label>
              <textarea
                data-testid="review-quote-input"
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                maxLength={800}
                rows={5}
                required
                placeholder="Tell other survivors how the fit, quality, print or shipping was..."
                className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-zinc-200 outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100 placeholder:text-zinc-600 resize-none"
              />
              <div className="mt-1 flex justify-between font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest">
                <span>Min 8 characters</span>
                <span>{form.quote.length}/800</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="review-submit-btn"
              className="bg-zinc-300 hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 px-6 py-3 font-display text-lg tracking-widest uppercase transition-colors"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReviewsPage;
